/**
 * Core logic for the Communication step: state, API hooks, template defaulting,
 * validation, and syncToPayload. Extracted from InterviewActivityCommunication
 * to keep the component focused on rendering.
 */
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Ref,
} from 'react';
import type { SelectOption } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityPayload } from '../InterviewActivity.interface';
import type { MutableRefObject } from 'react';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from './communication.types';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from './helpers/payloadMap';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from './communication.state';
import {
  getFirstCommunicationErrorScrollTarget,
  scrollCommunicationSectionIntoView,
} from './communication.scroll';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from './communication.validate';
import { calendarInviteTemplateNameForApi } from './helpers/calendarInviteName';
import { compositionFromTemplateOption } from './helpers/templateApply';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import {
  useInterviewActivityCommunicationVariables,
  useInterviewCommunicationSenderOptions,
  useInterviewEmailTemplateOptions,
} from './api';

function useFromAndCcBccOptions(delegatableOptions: SelectOption[]) {
  return useMemo(() => {
    const organizer: SelectOption = {
      value: INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE,
      label: 'Organizer email',
      description:
        'Whoever is scheduling the interview will be prefilled as the sender',
    };
    const rest = delegatableOptions.filter(
      (o) => o.value !== INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
    );
    return [organizer, ...rest];
  }, [delegatableOptions]);
}

interface UseCommunicationStepOptions {
  payloadRef: MutableRefObject<InterviewActivityPayload>;
  prefillVersion: number;
  ref: Ref<StepFormApi>;
}

export function useCommunicationStep({
  payloadRef,
  prefillVersion,
  ref,
}: UseCommunicationStepOptions) {
  // ── State ────────────────────────────────────────────────────────────────
  const [state, dispatch] = useReducer(
    interviewCommunicationReducer,
    initialInterviewCommunicationFormState,
    (base) =>
      hydrateCommunicationStateFromPayload(
        payloadRef.current.communication,
        base,
        { stripCalendarTemplates: prefillVersion > 0 }
      )
  );

  const [fieldErrors, setFieldErrors] =
    useState<InterviewCommunicationValidationErrors>({});

  // ── API hooks ────────────────────────────────────────────────────────────
  const { data: candidateTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('candidate');
  const { data: interviewerTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('interviewer');
  const { data: selfScheduleTemplateOptions = [] } =
    useInterviewEmailTemplateOptions('self-schedule');
  const { data: delegatableRaw = [] } =
    useInterviewCommunicationSenderOptions();
  const mergedParticipantOptions = useFromAndCcBccOptions(delegatableRaw);

  const {
    candidateCalendar: candidateCalendarVariables,
    interviewerCalendar: interviewerCalendarVariables,
    candidateEmail: candidateEmailVariables,
    interviewerEmail: interviewerEmailVariables,
    selfScheduleEmail: selfScheduleVariables,
  } = useInterviewActivityCommunicationVariables();

  const { mutateAsync: uploadUserInlineImageAsync } =
    useUploadUserInlineImage();

  const uploadInlineImage = useCallback(
    (file: File) => uploadUserInlineImageAsync(file),
    [uploadUserInlineImageAsync]
  );

  // ── Validation variables ─────────────────────────────────────────────────
  const validationVariables: CommunicationValidationVariables = useMemo(
    () => ({
      candidateCalendar: candidateCalendarVariables,
      interviewerCalendar: interviewerCalendarVariables,
      candidateEmail: candidateEmailVariables,
      interviewerEmail: interviewerEmailVariables,
      selfSchedule: selfScheduleVariables,
    }),
    [
      candidateCalendarVariables,
      candidateEmailVariables,
      interviewerCalendarVariables,
      interviewerEmailVariables,
      selfScheduleVariables,
    ]
  );

  /** After a failed submit, keep error UI in sync while the user fixes fields. */
  useEffect(() => {
    setFieldErrors((prev) => {
      if (!hasValidationErrors(prev)) return prev;
      return validateInterviewCommunicationForm(state, validationVariables);
    });
  }, [state, validationVariables]);

  // ── Template defaulting (one-shot per block) ─────────────────────────────
  const didDefaultCandidateCal = useRef(false);
  const didDefaultInterviewerCal = useRef(false);
  const didDefaultCandidateEmail = useRef(false);
  const didDefaultInterviewerEmail = useRef(false);
  const didDefaultSelfSchedule = useRef(false);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!candidateTemplateOptions.length) return;
    if (state.candidateCalendar.template) {
      didDefaultCandidateCal.current = true;
      return;
    }
    if (didDefaultCandidateCal.current) return;
    didDefaultCandidateCal.current = true;
    dispatch({
      type: 'SET_CANDIDATE_CALENDAR',
      payload: compositionFromTemplateOption(
        state.candidateCalendar,
        candidateTemplateOptions[0]
      ),
    });
  }, [prefillVersion, candidateTemplateOptions, state.candidateCalendar]);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!interviewerTemplateOptions.length) return;
    if (state.interviewerCalendar.template) {
      didDefaultInterviewerCal.current = true;
      return;
    }
    if (didDefaultInterviewerCal.current) return;
    didDefaultInterviewerCal.current = true;
    dispatch({
      type: 'SET_INTERVIEWER_CALENDAR',
      payload: compositionFromTemplateOption(
        state.interviewerCalendar,
        interviewerTemplateOptions[0]
      ),
    });
  }, [prefillVersion, interviewerTemplateOptions, state.interviewerCalendar]);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!state.candidateEmailEnabled || !candidateTemplateOptions.length) return;
    if (state.candidateEmail.template) {
      didDefaultCandidateEmail.current = true;
      return;
    }
    if (didDefaultCandidateEmail.current) return;
    didDefaultCandidateEmail.current = true;
    dispatch({
      type: 'SET_CANDIDATE_EMAIL',
      payload: compositionFromTemplateOption(
        state.candidateEmail,
        candidateTemplateOptions[0]
      ),
    });
  }, [
    prefillVersion,
    candidateTemplateOptions,
    state.candidateEmail,
    state.candidateEmailEnabled,
  ]);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!state.interviewerEmailEnabled || !interviewerTemplateOptions.length)
      return;
    if (state.interviewerEmail.template) {
      didDefaultInterviewerEmail.current = true;
      return;
    }
    if (didDefaultInterviewerEmail.current) return;
    didDefaultInterviewerEmail.current = true;
    dispatch({
      type: 'SET_INTERVIEWER_EMAIL',
      payload: compositionFromTemplateOption(
        state.interviewerEmail,
        interviewerTemplateOptions[0]
      ),
    });
  }, [
    prefillVersion,
    interviewerTemplateOptions,
    state.interviewerEmail,
    state.interviewerEmailEnabled,
  ]);

  useEffect(() => {
    if (prefillVersion > 0) return;
    if (!state.selfScheduleEnabled || !selfScheduleTemplateOptions.length)
      return;
    if (state.selfSchedule.template) {
      didDefaultSelfSchedule.current = true;
      return;
    }
    if (didDefaultSelfSchedule.current) return;
    didDefaultSelfSchedule.current = true;
    dispatch({
      type: 'SET_SELF_SCHEDULE',
      payload: compositionFromTemplateOption(
        state.selfSchedule,
        selfScheduleTemplateOptions[0]
      ),
    });
  }, [
    prefillVersion,
    selfScheduleTemplateOptions,
    state.selfSchedule,
    state.selfScheduleEnabled,
  ]);

  // ── Imperative handle (validate + syncToPayload) ─────────────────────────
  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        const errors = validateInterviewCommunicationForm(
          state,
          validationVariables
        );
        setFieldErrors(errors);
        if (!hasValidationErrors(errors)) return true;
        const target = getFirstCommunicationErrorScrollTarget(errors);
        if (target) {
          const needsTabSwitch = state.activeSubTab !== target.tab;
          if (needsTabSwitch) {
            dispatch({ type: 'SET_ACTIVE_SUB_TAB', payload: target.tab });
          }
          window.setTimeout(
            () => scrollCommunicationSectionIntoView(target.sectionId),
            needsTabSwitch ? 160 : 0
          );
        }
        return false;
      },
      syncToPayload: () => {
        const comm = toInterviewCommunicationPayload(state);
        comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
          state.candidateCalendar,
          candidateTemplateOptions
        );
        comm._ui_calendar_interviewer_name = calendarInviteTemplateNameForApi(
          state.interviewerCalendar,
          interviewerTemplateOptions
        );
        payloadRef.current.communication = comm;
      },
    }),
    [
      candidateTemplateOptions,
      dispatch,
      interviewerTemplateOptions,
      payloadRef,
      state,
      validationVariables,
    ]
  );

  // ── Panel props ──────────────────────────────────────────────────────────
  const panelVariableProps = useMemo(
    () => ({
      candidateCalendarVariables,
      interviewerCalendarVariables,
      candidateEmailVariables,
      interviewerEmailVariables,
      selfScheduleVariables,
      uploadInlineImage,
    }),
    [
      candidateCalendarVariables,
      candidateEmailVariables,
      interviewerCalendarVariables,
      interviewerEmailVariables,
      selfScheduleVariables,
      uploadInlineImage,
    ]
  );

  return {
    state,
    dispatch,
    fieldErrors,
    candidateTemplateOptions,
    interviewerTemplateOptions,
    selfScheduleTemplateOptions,
    mergedParticipantOptions,
    panelVariableProps,
  };
}
